/**
 * Типы предметной области, общие для бота и веб-админки.
 *
 * Договорённости по времени: все моменты времени хранятся и передаются
 * строками ISO-8601 в UTC (суффикс `Z`). Перевод в часовой пояс сообщества
 * происходит только на границе отображения — см. `server/src/domain/time.ts`.
 */

/** Discord snowflake — идентификатор пользователя, канала, роли или сообщения. */
export type Snowflake = string;

/** Момент времени в UTC, ISO-8601, например `2026-08-07T18:15:00.000Z`. */
export type IsoTimestamp = string;

/**
 * Тип мероприятия. Свободная строка, а не объединение литералов: набор типов
 * настраивается в админке, поэтому новый тип не должен требовать правки кода.
 */
export type EventType = string;

/** Типы мероприятий, с которыми проект работал исторически. */
export const DEFAULT_EVENT_TYPES = ['MCL', 'CAPTURE', 'VZZ'] as const;

/**
 * Типы, которые учитываются в колонке «MCL+VZZ» статистики.
 * CAPTURE считается отдельно.
 */
export const MCL_VZZ_EVENT_TYPES = ['MCL', 'VZZ'] as const;

/** Список, в котором находится участник набора. */
export type MemberList = 'plus' | 'reserve';

/**
 * Состояние набора.
 *
 * - `open` — идёт приём заявок, кнопки в Discord активны;
 * - `closed` — приём закрыт (автоматически в момент старта либо вручную
 *   модератором); состав зафиксирован и правится только из админки;
 * - `cancelled` — набор отменён, в статистике не учитывается.
 */
export type EventStatus = 'open' | 'closed' | 'cancelled';

/**
 * Итог мероприятия. Отмечается после закрытия набора, до этого `null`:
 * у идущего набора итога ещё нет, а у отменённого не будет.
 */
export type EventResult = 'win' | 'loss' | 'afk';

/** Все итоги по порядку — для разбора ввода и фильтров. */
export const EVENT_RESULTS: readonly EventResult[] = ['win', 'loss', 'afk'];

export const EVENT_RESULT_LABEL: Readonly<Record<EventResult, string>> = {
  win: 'Победа',
  loss: 'Поражение',
  // Бой не состоялся или в нём не участвовали: нейтральный итог, в винрейт
  // не идёт ни победой, ни поражением.
  afk: 'АФК',
};

/** Статус рассмотрения заявки в семью. */
export type FamilyApplicationStatus = 'pending' | 'accepted' | 'rejected';

/** Результат попытки отправить личное сообщение. */
export type DmDeliveryStatus = 'sent' | 'failed' | 'skipped';

/** Тир игрока: 1..4, либо 0 — если тир не назначен. */
export type Tier = 0 | 1 | 2 | 3 | 4;

/** Все тиры по возрастанию номера: первый — сильнейший состав. */
export const TIERS = [1, 2, 3, 4] as const satisfies readonly Tier[];

/**
 * Приводит число из базы к тиру: всё, чего нет в `TIERS`, — «тира нет».
 *
 * Одна функция на весь проект намеренно. Раньше эта проверка жила копиями
 * в трёх репозиториях, и добавление четвёртого тира их развело: в базе
 * лежала четвёрка, а наружу шёл ноль — «тира нет» у того, у кого он есть.
 */
export function toTier(value: number): Tier {
  return (TIERS as readonly number[]).includes(value) ? (value as Tier) : 0;
}

/** Назначенный тир: то же, что `Tier`, но без «тира нет». */
export type AssignedTier = (typeof TIERS)[number];

/**
 * Подписи тиров — то, как тир называется для человека.
 *
 * Ключ — номер тира (это идентификатор: колонка в базе, ключ привязки
 * `roles.tierN`, переменная `TIER_N_ROLE_ID`), значение — имя роли на
 * сервере. Совпадать они не обязаны: четвёртый тир называется `plus`,
 * номером 4 при этом быть не перестаёт.
 *
 * Словарь один на весь проект по той же причине, что и `toTier` выше:
 * подпись собиралась в пятнадцати местах тремя разными способами, и роль,
 * переименованная сообществом, разошлась с интерфейсом молча.
 */
const TIER_LABEL: Readonly<Record<AssignedTier, string>> = {
  1: 'tier 1',
  2: 'tier 2',
  3: 'tier 3',
  4: 'plus',
};

/**
 * Уплотнённые подписи — там, где строка считается по символам.
 *
 * У четвёртого тира это `+`, а не `plus`: в составе набора подпись стоит
 * последним полем строки, а сам состав делится на «основной» и «замену», и
 * слово `plus` в конце строки читалось бы как «он в основном составе».
 * Знак `+` этой двусмысленности не создаёт и не ломает ряд кнопок
 * переключателя, рассчитанный на один знак.
 */
const TIER_SHORT_LABEL: Readonly<Record<AssignedTier, string>> = {
  1: 'T1',
  2: 'T2',
  3: 'T3',
  4: '+',
};

/**
 * Подпись тира. `short` — для состава в Discord и переключателя в строке.
 *
 * «Тира нет» здесь намеренно нет: на каждом экране для нуля своё слово
 * («без тира», «Без тира», «—», пустая ячейка в выгрузке), и сводить их
 * вместе — отдельная задача. Тип без нуля к тому же заставляет вызывающего
 * оставить свою ветку, а она есть во всех местах и сейчас.
 */
export function tierLabel(tier: AssignedTier, style: 'full' | 'short' = 'full'): string {
  return style === 'short' ? TIER_SHORT_LABEL[tier] : TIER_LABEL[tier];
}

/**
 * Как назвать «любую тир-роль» в тексте про доступ.
 *
 * Отдельной строкой, а не сборкой из `tierLabel`: словесный диапазон
 * «1–3» из подписей не складывается, а расходиться с ними ему нельзя.
 * Через косую черту, а не «или»: на экране закрытого раздела эта строка
 * склеивается с другими ролями через « и », и внутренний союз ломал бы
 * предложение.
 */
export const TIER_ROLES_LABEL = 'tier 1–3/plus';

/**
 * Сколько человек можно выбрать для рассылки поимённо за раз.
 *
 * Предел не от Discord, а от здравого смысла: поимённый выбор нужен, чтобы
 * написать десятку-другому, и список на три сотни означает промах по кнопке
 * «выбрать всех показанных», а не намерение.
 *
 * Лежит в общем пакете, чтобы админка не давала набрать больше, чем примет
 * сервер: иначе человек отмечает пять сотен, а узнаёт об этом отказом.
 */
export const BROADCAST_PICK_LIMIT = 300;

/** Анкета игрока. */
export interface Profile {
  userId: Snowflake;
  gameNick: string;
  staticId: string;
  /** Тир по ролям Discord: 0 — тира нет. Обновляется при смене ролей. */
  tier: Tier;
  /** Имя в Discord. `null` — бот его ещё ни разу не видел. */
  discordName: string | null;
  /** Когда человек покинул сервер Discord. `null` — он на сервере. */
  leftAt: IsoTimestamp | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Набор на мероприятие. */
export interface RosterEvent {
  id: number;
  eventType: EventType;
  status: EventStatus;
  /** Итог мероприятия. `null` — если он ещё не отмечен. */
  result: EventResult | null;
  /** Тера ВЗП, отмеченная после боя. `null` — не отмечена. */
  territory: string | null;
  /** Время начала в UTC. `null` — если набор создан без времени. */
  startsAt: IsoTimestamp | null;
  description: string | null;
  capacity: number;
  channelId: Snowflake;
  messageId: Snowflake | null;
  /** Сообщение со списком состава: отдельное, чтобы правки не звали всех. */
  rosterMessageId: Snowflake | null;
  modChannelId: Snowflake;
  modMessageId: Snowflake | null;
  /** Карта мероприятия: ключ из `maps.ts`. `null` — карта не выбрана. */
  mapId: string | null;
  reminderSentAt: IsoTimestamp | null;
  /** Когда по набору проверяли сбор в голосовом. `null` — ещё не проверяли. */
  voiceCheckedAt: IsoTimestamp | null;
  rollbackProcessedAt: IsoTimestamp | null;
  /** Сколько раз рассылка запросов на откаты завершилась с ошибками. */
  rollbackAttempts: number;
  closedAt: IsoTimestamp | null;
  createdBy: Snowflake;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Участник набора. */
export interface EventMember {
  eventId: number;
  userId: Snowflake;
  list: MemberList;
  tier: Tier;
  joinedAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Участник набора вместе с данными его анкеты — то, что показывают списки. */
export interface EventMemberWithProfile extends EventMember {
  gameNick: string;
  staticId: string;
}

/** Заявка на вступление в семью. */
export interface FamilyApplication {
  id: number;
  userId: Snowflake;
  gameNick: string;
  age: number;
  clips: string;
  adequacy: number;
  status: FamilyApplicationStatus;
  reviewChannelId: Snowflake | null;
  reviewMessageId: Snowflake | null;
  /**
   * Личный канал рассмотрения заявки. У старых заявок его нет: их карточки
   * жили в общем канале, который при решении удалять нельзя.
   */
  ticketChannelId: Snowflake | null;
  reviewerId: Snowflake | null;
  reviewComment: string | null;
  dmStatus: string | null;
  createdAt: IsoTimestamp;
  reviewedAt: IsoTimestamp | null;
  /**
   * Когда можно подать новую заявку. `null` — хоть сейчас.
   *
   * Хранится на заявке, а не считается от настройки: отказы бывают разные,
   * и модератор решает при отказе, ждать человеку неделю или нет.
   */
  reapplyAt: IsoTimestamp | null;
  /** Кто позвал на обзвон. `null` — не звали. */
  callInvitedBy: Snowflake | null;
  callInvitedAt: IsoTimestamp | null;
  updatedAt: IsoTimestamp;
}

/** Персональный канал игрока для сдачи откатов. */
export interface RollbackChannel {
  userId: Snowflake;
  channelId: Snowflake;
  /**
   * Приветствие бота в этом канале.
   *
   * `null` — канал заведён до того, как идентификатор начали хранить: там
   * приветствие ищется по тексту и запоминается при первой же встрече.
   */
  introMessageId: Snowflake | null;
  createdAt: IsoTimestamp;
  updatedAt: IsoTimestamp;
}

/** Запрос на откат, отправленный игроку после старта мероприятия. */
export interface RollbackRequest {
  eventId: number;
  userId: Snowflake;
  channelId: Snowflake;
  messageId: Snowflake;
  requestedAt: IsoTimestamp;
  /** Заполняется, когда игрок прислал ссылку в свой канал откатов. */
  submittedAt: IsoTimestamp | null;
  submittedUrl: string | null;
}

/** Статистика игрока по прошедшим мероприятиям. */
export interface PlayerStats {
  userId: Snowflake;
  gameNick: string;
  staticId: string;
  /** Посещения мероприятий типов MCL и VZZ в основном составе. */
  mclVzzCount: number;
  /** Посещения CAPTURE в основном составе. */
  captureCount: number;
  /** Сколько раз игрок был в основном составе. */
  mainCount: number;
  /** Сколько раз игрок остался в замене. */
  reserveCount: number;
  /** Тир по ролям Discord: 0 — тира нет. */
  tier: Tier;
  /** Имя в Discord. `null` — бот его ещё ни разу не видел. */
  discordName: string | null;
  /** Аватарка из Discord. `null` — бот ещё не сверял анкеты либо человек вышел. */
  avatarUrl: string | null;
  /** Когда человек покинул сервер Discord. `null` — он на сервере. */
  leftAt: IsoTimestamp | null;
  updatedAt: IsoTimestamp;
}

/**
 * Боевые цифры игрока по каптам — то, чем дополняется строка в базе игроков.
 *
 * Считаются по статику, а не по идентификатору Discord: в ответе игровой
 * службы никакого Discord нет, статик — единственное, что связывает человека
 * в анкете с человеком в бою. У кого каптов не было вовсе, этих цифр нет
 * совсем (`null` в строке), а не нули: ноль урона и «не выходил» — разные
 * вещи, и фильтр «слабый» не должен ловить тех, кто просто не ходил.
 */
export interface PlayerCombat {
  captures: number;
  damage: number;
  kills: number;
  /** Средние за капт — считаются на сервере, чтобы делить в одном месте. */
  avgDamage: number;
  avgKills: number;
  wins: number;
  losses: number;
  /** Смерти и K/D — с vzp-gta5rp.com. `null` — сайт игрока ещё не прислал. */
  deaths: number | null;
  kd: number | null;
  /** `null` — цифры есть только с сайта, а в боях, записанных ботом, человека нет. */
  lastCaptureAt: IsoTimestamp | null;
  /** Каптов за последние семь дней. */
  capturesWeek: number;
  /** Каптов за последние тридцать дней. */
  capturesMonth: number;
}

/**
 * Сколько каптов было у семьи за те же окна.
 *
 * Знаменатель к цифрам игрока: «4 капта за месяц» ничего не значит, пока
 * неизвестно, было их всего пять или сорок.
 */
export interface CaptureActivity {
  week: number;
  month: number;
}

/** Сводные показатели для главной страницы админки. */
export interface StatsSummary {
  profileCount: number;
  /** Победы среди состоявшихся мероприятий с отмеченным итогом. */
  winCount: number;
  /** Поражения среди них же. */
  lossCount: number;
  pendingApplicationCount: number;
}

/**
 * Боевые показатели игрока за мероприятия.
 *
 * Источник — внешний сервис, которого пока нет: цифры появятся, когда будет
 * ключ доступа. До тех пор карточка показывает блок пустым, а не прячет его —
 * так видно, что место для показателей предусмотрено, а не забыто.
 */
export interface PlayerCombatStats {
  /** Кто дал цифры. Спорное значение должно быть где искать. */
  source: string;
  /** Когда данные забирали в последний раз. */
  updatedAt: IsoTimestamp;
  /**
   * Сколько каптов вошло в расчёт.
   *
   * Только капты: MCL и VZZ игровая служба не считает, и подмешивать их сюда
   * значило бы делить урон на мероприятия, которых в цифрах нет.
   */
  captures: number;
  kills: number;
  damage: number;
  /** Средние за капт. */
  avgKills: number;
  avgDamage: number;
  /** Победы, смерти и K/D. `null` — источник их не отдаёт (записанные бои без сайта). */
  wins: number | null;
  deaths: number | null;
  kd: number | null;
  /** Сервер, по которому цифры. `null` — все бои вместе. */
  server: string | null;
}

/** Присланный откат — строка в карточке. */
export interface PlayerRollback {
  url: string;
  /** Название ролика или файла. `null` — узнать не удалось. */
  title: string | null;
  postedAt: IsoTimestamp;
}

/** Один капт в истории игрока — строка в карточке. */
export interface PlayerCaptureEntry {
  captureId: number;
  startedAt: IsoTimestamp;
  /** Зона, за которую шёл бой. `null` — служба её не назвала. */
  gangZoneId: number | null;
  /** Кто был по ту сторону. `null` — наши не были ни одной из сторон. */
  opponent: string | null;
  /** Исход с точки зрения нашей семьи. */
  outcome: 'win' | 'loss' | 'unknown';
  damage: number;
  kills: number;
}

/** Участие игрока в одном мероприятии — строка истории в карточке. */
export interface PlayerEventEntry {
  eventId: number;
  eventType: EventType;
  startsAt: IsoTimestamp | null;
  status: EventStatus;
  result: EventResult | null;
  list: MemberList;
}

/** Всё, что показывает карточка игрока. */
export interface PlayerCard {
  player: PlayerStats;
  /** Мероприятия от свежих к старым. */
  history: PlayerEventEntry[];
  /** `null` — показателей нет, причину объясняет `combatAbsence`. */
  combat: PlayerCombatStats | null;
  /** Капты игрока от свежих к старым. Пусто — ни одного ещё не сыграно. */
  captures: PlayerCaptureEntry[];
  /**
   * Почему боевых показателей нет. Различать важно: «источник не подключён» —
   * это про настройку сообщества, а «нет данных» — про конкретного игрока, и
   * подсказки в карточке должны звучать по-разному.
   */
  combatAbsence: 'not-configured' | 'no-data' | null;
  /** Откаты от свежих к старым. */
  rollbacks: PlayerRollback[];
  /** Ссылка на ветку откатов игрока в Discord. `null` — ветки ещё нет. */
  rollbackThreadUrl: string | null;
}
